import {
    useCallback,
    useEffect,
    useRef,
    useState,
    type ComponentProps,
} from "react";
import classNames from "classnames";
// eslint-disable-next-line no-restricted-imports -- the design-system wrapper around Base UI
import { Tooltip as TooltipBase } from "@base-ui/react/tooltip";
import { isDisabledElement } from "@design-system/overlay/isDisabledElement";
import { useIsOverlayDeferred } from "@design-system/overlay/overlayDeferral";
import type { TooltipProps } from "./Tooltip.types";
import {
    isTooltipBodyEmpty,
    TooltipBody,
    tooltipLayout,
    type TooltipLayout,
} from "./TooltipBody";
import { useCloseOnContainerScroll } from "./useCloseOnContainerScroll";
import styles from "./Tooltip.module.scss";

export type {
    PresenceTooltipRow,
    TooltipProps,
    TooltipRow,
    TooltipSections,
} from "./Tooltip.types";

export const TOOLTIP_OPEN_DELAY_MS = 100;
const SIDE_OFFSET_PX = 8;
const COLLISION_PADDING_PX = 8;

const layoutClassNames: Record<TooltipLayout, string | undefined> = {
    stack: undefined,
    shortcut: styles.shortcutRow,
    shortcutOnly: styles.shortcutOnly,
    presence: styles.presence,
};

/**
 * Delay group for every tooltip: 100ms to open, 0ms to close, and instant
 * neighbours once one is open. Mounted once at the app root (and in tests).
 */
export const TooltipProvider = ({
    delay = TOOLTIP_OPEN_DELAY_MS,
    closeDelay = 0,
    ...props
}: ComponentProps<typeof TooltipBase.Provider>) => (
    <TooltipBase.Provider delay={delay} closeDelay={closeDelay} {...props} />
);

/**
 * Plain-text hint for its child element, per Figma's Tooltip set, or the
 * presence rows of who is on an item (Figma 4822:8969). With nothing
 * to show it stays closed and keeps the child mounted. A disabled child is wrapped
 * in a span so hover still reaches the tooltip. With `anchor` instead of a
 * child, the caller drives `open` and the popup points at that element.
 * Under a deferring `OverlayDeferralContext` it renders that trigger alone.
 */
export const Tooltip = ({
    children,
    anchor,
    side = "top",
    align = "center",
    disabled,
    open,
    onOpenChange,
    trackCursorAxis = "none",
    className,
    content,
    title,
    sections,
    bullets,
    shortcut,
    presence,
}: TooltipProps) => {
    const triggerRef = useRef<HTMLElement | null>(null);
    const setTriggerElement = useCallback((element: HTMLElement | null) => {
        triggerRef.current = element;
    }, []);
    const isAnchored = anchor !== undefined;
    useEffect(() => {
        if (isAnchored) {
            triggerRef.current = anchor;
        }
    }, [anchor, isAnchored]);
    const [isOpenUncontrolled, setIsOpenUncontrolled] = useState(false);
    const isControlled = open !== undefined;
    const isOpen = open ?? isOpenUncontrolled;

    const handleOpenChange = useCallback(
        (nextIsOpen: boolean) => {
            if (!isControlled) {
                setIsOpenUncontrolled(nextIsOpen);
            }
            onOpenChange?.(nextIsOpen);
        },
        [isControlled, onOpenChange],
    );
    const close = useCallback(
        () => handleOpenChange(false),
        [handleOpenChange],
    );

    useCloseOnContainerScroll({ triggerRef, isOpen, onClose: close });
    const isDeferred = useIsOverlayDeferred();

    const body = { content, title, sections, bullets, shortcut, presence };
    // An empty body disables the root instead of dropping it, so a trigger
    // whose tooltip text comes and goes keeps its element (and focus).
    const isEmpty = isTooltipBodyEmpty(body);

    const trigger =
        children && !isEmpty && isDisabledElement(children) ? (
            <span className={styles.disabledTrigger}>{children}</span>
        ) : (
            children
        );

    if (isDeferred) {
        return trigger ?? null;
    }

    return (
        <TooltipBase.Root
            open={isOpen && !isEmpty}
            onOpenChange={handleOpenChange}
            disabled={disabled === true || isEmpty}
            disableHoverablePopup
            trackCursorAxis={trackCursorAxis}
        >
            {isAnchored ? null : (
                <TooltipBase.Trigger ref={setTriggerElement} render={trigger} />
            )}
            <TooltipBase.Portal>
                <TooltipBase.Positioner
                    className={styles.positioner}
                    anchor={anchor}
                    side={side}
                    align={align}
                    sideOffset={SIDE_OFFSET_PX}
                    collisionPadding={COLLISION_PADDING_PX}
                >
                    <TooltipBase.Popup
                        className={classNames(
                            styles.popup,
                            layoutClassNames[tooltipLayout(body)],
                            className,
                        )}
                    >
                        <TooltipBody {...body} />
                    </TooltipBase.Popup>
                </TooltipBase.Positioner>
            </TooltipBase.Portal>
        </TooltipBase.Root>
    );
};
